import { useEffect, useState } from "react";
import { useErrorHandler } from "../../hooks/useErrorHandler";
import { ErrorMessage } from "../../components/error-message";
import CrudTable from "../../components/crud-table";
import {
  PaymentStatus,
  PaymentStatusColors,
  PaymentStatusTranslations,
  PaymentType,
  PaymentTypeColors,
  PaymentTypeTranslations,
  PaymentWithDetails,
} from "../../types/payments.types";
import { PaymentsAPI } from "../../APIs/payments.api";
import { CrudTableHeader } from "../../components/crud-table-header";
import { useTableControls } from "../../hooks/useTableControls";
import { useCompetitions } from "../../context/CompetitionsContext";
import Label from "../../components/label";
import { WalletIcon } from "@heroicons/react/24/outline";
import { TeamLink } from "../../components/team-link";
import { Loading } from "../../components/loading";
import { useNavigate } from "react-router-dom";

export function PaymentsTableAdmins() {
  const [payments, setPayments] = useState<PaymentWithDetails[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { error, handleError, clearError } = useErrorHandler();
  const { currentCompetitionId } = useCompetitions();
  const navigate = useNavigate();

  const {
    paginatedData: paginatedPayments,
    filteredData,
    totalPages,
    currentPage,
    sortConfig,
    setSortConfig,
    setSearchQuery,
    setSelectedFilters,
    setCurrentPage,
  } = useTableControls<PaymentWithDetails>(payments || []);

  const columns = [
    {
      key: "payment_id" as keyof PaymentWithDetails,
      label: "N° Pago",
      sortable: true,
    },
    {
      key: "team_name" as keyof PaymentWithDetails,
      label: "Equipo",
      sortable: true,
      render: (value: string, row: PaymentWithDetails) => (
        <TeamLink teamName={value} teamId={row.team_id} />
      ),
    },
    {
      key: "registration_number" as keyof PaymentWithDetails,
      label: "Nº Reg.",
      sortable: true,
    },
    {
      key: "team_number" as keyof PaymentWithDetails,
      label: "Nº Equipo",
      sortable: true,
      render: (value: number | null) => value || "-",
    },
    {
      key: "payment_type" as keyof PaymentWithDetails,
      label: "Tipo",
      sortable: true,
      render: (value: PaymentType) => (
        <Label color={PaymentTypeColors[value] as any}>
          {PaymentTypeTranslations[value]}
        </Label>
      ),
    },
    {
      key: "payment_status" as keyof PaymentWithDetails,
      label: "Estado",
      sortable: true,
      render: (value: string) => (
        <Label color={PaymentStatusColors[value as PaymentStatus]}>
          {PaymentStatusTranslations[value as PaymentStatus] || value}
        </Label>
      ),
    },
    {
      key: "payment_amount" as keyof PaymentWithDetails,
      label: "Monto",
      sortable: true,
      render: (value: string | null, row: PaymentWithDetails) => {
        const amount = value || row.payment_attempt_amount;
        return amount ? (
          <Label color="gray">
            $ {parseFloat(String(amount)).toLocaleString("es-AR")}
          </Label>
        ) : (
          "-"
        );
      },
    },
    {
      key: "payment_updated_at" as keyof PaymentWithDetails,
      label: "Fecha",
      sortable: true,
      render: (value: string) =>
        new Date(value).toLocaleString("es-AR", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        }),
    },
    {
      key: "mercadopago_payment_id" as keyof PaymentWithDetails,
      label: "ID MP",
      sortable: true,
      render: (value: string | null, row: PaymentWithDetails) => {
        if (row.payment_type === PaymentType.MERCADOPAGO && value) {
          return value;
        }

        return "-";
      },
    },
  ];

  useEffect(() => {
    if (!currentCompetitionId) {
      return;
    }
    const fetchPayments = async () => {
      clearError();
      setIsLoading(true);
      try {
        const response = await PaymentsAPI.getAllPayments(currentCompetitionId);
        if (response.statusCode === 200) {
          setPayments(response.data);
        } else {
          handleError(response.message, { persist: true });
        }
      } catch (err) {
        handleError("Error de conexión, intente nuevamente.", {
          persist: true,
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchPayments();
  }, [handleError, currentCompetitionId, clearError]);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  const handleSort = (
    sortBy: string | null,
    sortDirection: "asc" | "desc" | null
  ) => {
    setSortConfig({
      sortBy: sortBy as keyof PaymentWithDetails | null,
      sortDirection,
    });
  };
  const handleFilterChange = (selectedFilters: Record<string, string[]>) => {
    setSelectedFilters(selectedFilters);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <div className="w-full p-8">
      <CrudTableHeader
        title="Pagos"
        count={payments?.length}
        icon={<WalletIcon />}
      />

      {error && (
        <div className="mb-4">
          <ErrorMessage message={error} dismissible />
        </div>
      )}

      {isLoading ? (
        <Loading />
      ) : payments ? (
        <CrudTable
          data={paginatedPayments}
          totalCount={filteredData.length}
          columns={columns}
          search={{
            onSearch: handleSearch,
            placeholder: "Buscar pagos...",
          }}
          sort={{
            sortBy: sortConfig.sortBy,
            sortDirection: sortConfig.sortDirection,
            onSort: handleSort,
          }}
          pagination={{
            page: currentPage,
            totalPages: totalPages,
            onPageChange: handlePageChange,
          }}
          filters={[
            {
              key: "payment_status",
              label: "Estado",
              multiple: true,
              options: Object.entries(PaymentStatusTranslations).map(
                ([statusId, statusName]) => ({
                  key: String(statusId),
                  value: statusName,
                })
              ),
            },
            {
              key: "payment_type",
              label: "Tipo",
              multiple: true,
              options: Object.entries(PaymentTypeTranslations).map(
                ([typeId, typeName]) => ({
                  key: String(typeId),
                  value: typeName,
                })
              ),
            },
          ]}
          onFilterChange={handleFilterChange}
          actions={{
            view: {
              label: "",
              onClick: (row: PaymentWithDetails) =>
                navigate(`view/${row.payment_id}`),
            },
          }}
        />
      ) : null}
    </div>
  );
}
